import { BaseFeature } from '../../core/base-feature.js';

/**
 * SpinWheelFeature - Frontend feature for Spin the Wheel punishment
 * Allows admin to configure wheel, trigger spins, handle plea requests,
 * manage escalation, jackpot, and multi-spin combos.
 */
export class SpinWheelFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.wheelConfig = {
            segments: [],
            spinDuration: 7000,
            escalationEnabled: false,
            escalationLastSeverity: 0,
            delaySeconds: 0,
            jackpotState: null
        };
        this.currentSpin = null;
        this.spinHistory = [];
        this.pendingPlea = null;

        // Event callbacks for React components
        this.spinResultCallbacks = new Set();
        this.spinProgressCallbacks = new Set();
        this.multiSpinResultCallbacks = new Set();
        this.pleaRequestCallbacks = new Set();
        this.updateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'spin-wheel',
            name: 'Spin the Wheel',
            version: 'v2.0',
            tier: 'external',
            capabilities: [
                'configure', 'spin', 'get_config', 'get_history', 'get_state',
                'approve_plea', 'deny_plea', 'reset_escalation', 'configure_jackpot'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[SpinWheel] Initializing...');
        console.log('[SpinWheel] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId, fromId } = message;
        const command = cmd || action;

        switch (command) {
            case 'spin_started':
                return this.handleSpinStarted(payload);
            case 'spin_progress':
                return this.handleSpinProgress(payload);
            case 'spin_result':
                return this.handleSpinResult(payload);
            case 'multi_spin_result':
                return this.handleMultiSpinResult(payload);
            case 'plea_request':
                return this.handlePleaRequest(payload);
            case 'initial_state':
                return this.handleInitialState(payload);

            // Responses
            case 'configure_response':
                return this.handleResponse('configure', payload);
            case 'get_config_response':
                return this.handleResponse('get_config', payload);
            case 'spin_response':
                return this.handleResponse('spin', payload);
            case 'get_history_response':
                return this.handleResponse('get_history', payload);
            case 'get_state_response':
                return this.handleResponse('get_state', payload);
            case 'approve_plea_response':
                return this.handleResponse('approve_plea', payload);
            case 'deny_plea_response':
                return this.handleResponse('deny_plea', payload);
            case 'reset_escalation_response':
                return this.handleResponse('reset_escalation', payload);
            case 'configure_jackpot_response':
                return this.handleResponse('configure_jackpot', payload);

            default:
                console.warn(`[SpinWheel] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Real-time Event Handlers
    // =====================================

    handleSpinStarted(payload) {
        this.currentSpin = payload;
        this.notifyUpdateListeners();
    }

    handleSpinProgress(payload) {
        this.notifySpinProgressListeners(payload);
    }

    handleSpinResult(payload) {
        console.log('[SpinWheel] Spin result:', payload.result);
        this.spinHistory.unshift({ ...payload, timestamp: Date.now() });
        if (this.spinHistory.length > 50) this.spinHistory.pop();
        this.currentSpin = null;
        this.notifySpinResultListeners(payload);
        this.notifyUpdateListeners();
    }

    handleMultiSpinResult(payload) {
        console.log('[SpinWheel] Multi-spin complete:', payload.results?.length, 'results');
        for (const cb of this.multiSpinResultCallbacks) {
            try { cb(payload); } catch (e) { console.error('[SpinWheel] Multi-spin callback error:', e); }
        }
        this.notifyUpdateListeners();
    }

    handlePleaRequest(payload) {
        console.log('[SpinWheel] Plea request received');
        this.pendingPlea = payload;
        for (const cb of this.pleaRequestCallbacks) {
            try { cb(payload); } catch (e) { console.error('[SpinWheel] Plea callback error:', e); }
        }
        this.notifyUpdateListeners();
    }

    handleInitialState(payload) {
        console.log('[SpinWheel] Received initial state');
        if (payload.config) {
            this.wheelConfig = payload.config;
        }
        if (payload.state) {
            if (payload.state.config) this.wheelConfig = payload.state.config;
            if (payload.state.pendingPlea) this.pendingPlea = payload.state.pendingPlea;
        }
        if (payload.history) {
            this.spinHistory = payload.history;
        }
        this.notifyUpdateListeners();
    }

    // =====================================
    // Generic Response Handler
    // =====================================

    handleResponse(requestType, payload) {
        if (requestType === 'get_config' && payload?.success && payload.config) {
            this.wheelConfig = payload.config;
        }
        if (requestType === 'get_state' && payload?.success && payload.state) {
            if (payload.state.config) this.wheelConfig = payload.state.config;
            if (payload.state.pendingPlea) this.pendingPlea = payload.state.pendingPlea;
            else this.pendingPlea = null;
        }
        if (requestType === 'deny_plea' && payload?.success) {
            this.pendingPlea = null;
        }

        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // Commands
    // =====================================

    async configure(config) {
        if (config.segments) this.wheelConfig.segments = config.segments;
        if (config.spinDuration) this.wheelConfig.spinDuration = config.spinDuration;
        if (typeof config.escalationEnabled === 'boolean') this.wheelConfig.escalationEnabled = config.escalationEnabled;
        if (typeof config.delaySeconds === 'number') this.wheelConfig.delaySeconds = config.delaySeconds;

        return new Promise((resolve) => {
            this.sendMessage('configure', config);
            this._pendingRequests.set('configure', resolve);
        });
    }

    async getConfig() {
        return new Promise((resolve) => {
            this.sendMessage('get_config', {});
            this._pendingRequests.set('get_config', resolve);
        });
    }

    async getState() {
        return new Promise((resolve) => {
            this.sendMessage('get_state', {});
            this._pendingRequests.set('get_state', resolve);
        });
    }

    async spin(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('spin', options);
            this._pendingRequests.set('spin', resolve);
        });
    }

    async getHistory(limit = 20) {
        return new Promise((resolve) => {
            this.sendMessage('get_history', { limit });
            this._pendingRequests.set('get_history', resolve);
        });
    }

    async approvePlea(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('approve_plea', options);
            this._pendingRequests.set('approve_plea', resolve);
        });
    }

    async denyPlea() {
        this.pendingPlea = null;
        this.notifyUpdateListeners();
        return new Promise((resolve) => {
            this.sendMessage('deny_plea', {});
            this._pendingRequests.set('deny_plea', resolve);
        });
    }

    async resetEscalation() {
        return new Promise((resolve) => {
            this.sendMessage('reset_escalation', {});
            this._pendingRequests.set('reset_escalation', resolve);
        });
    }

    async configureJackpot(config) {
        return new Promise((resolve) => {
            this.sendMessage('configure_jackpot', config);
            this._pendingRequests.set('configure_jackpot', resolve);
        });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    onSpinResult(callback) {
        this.spinResultCallbacks.add(callback);
        return () => this.spinResultCallbacks.delete(callback);
    }

    onSpinProgress(callback) {
        this.spinProgressCallbacks.add(callback);
        return () => this.spinProgressCallbacks.delete(callback);
    }

    onMultiSpinResult(callback) {
        this.multiSpinResultCallbacks.add(callback);
        return () => this.multiSpinResultCallbacks.delete(callback);
    }

    onPleaRequest(callback) {
        this.pleaRequestCallbacks.add(callback);
        return () => this.pleaRequestCallbacks.delete(callback);
    }

    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifySpinResultListeners(data) {
        for (const cb of this.spinResultCallbacks) {
            try { cb(data); } catch (e) { console.error('[SpinWheel] Result callback error:', e); }
        }
    }

    notifySpinProgressListeners(data) {
        for (const cb of this.spinProgressCallbacks) {
            try { cb(data); } catch (e) { console.error('[SpinWheel] Progress callback error:', e); }
        }
    }

    notifyUpdateListeners() {
        const data = {
            config: this.wheelConfig,
            currentSpin: this.currentSpin,
            history: this.spinHistory,
            pendingPlea: this.pendingPlea
        };
        for (const cb of this.updateCallbacks) {
            try { cb(data); } catch (e) { console.error('[SpinWheel] Update callback error:', e); }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getWheelConfig() { return this.wheelConfig; }
    getCurrentSpin() { return this.currentSpin; }
    getSpinHistory() { return this.spinHistory; }
    getPendingPlea() { return this.pendingPlea; }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[SpinWheel] Cleaning up...');
        this.wheelConfig = { segments: [], spinDuration: 7000 };
        this.currentSpin = null;
        this.spinHistory = [];
        this.pendingPlea = null;
        this.spinResultCallbacks.clear();
        this.spinProgressCallbacks.clear();
        this.multiSpinResultCallbacks.clear();
        this.pleaRequestCallbacks.clear();
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
